import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Activity, Loader2, Database as DbIcon, CheckCircle2, Clock, Radio } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface ConnectionSession {
  database: string;
  state: string;
  query: string;
  user: string;
  service: string;
  duration_sec: number;
  avg_time_ms?: number;
  total_time_ms?: number;
}

interface ConnectionsTabProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${
              isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
            }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700 dark:text-gray-200 text-[9px] truncate max-w-[130px]" title={entry.value}>
              {entry.value.replace(/_/g, ' ')}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[130px] z-50">
        {label !== undefined && label !== null && label !== "" && (
          <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
            {label}
          </div>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color || entry.stroke || entry.fill || "#f97316" }}
              />
              {entry.name || entry.dataKey}
            </span>
            <span className="font-bold text-gray-900 dark:text-white font-mono">
              {typeof entry.value === 'number' ? entry.value.toLocaleString() : (entry.value ?? '-')}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const ConnectionsTab: React.FC<ConnectionsTabProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType,
}) => {
  const [loading, setLoading] = useState(true);
  const [metricsData, setMetricsData] = useState<any>(null);
  const [connTrendData, setConnTrendData] = useState<any>(null);
  const [perDbConnData, setPerDbConnData] = useState<any>(null);
  const [sessions, setSessions] = useState<ConnectionSession[]>([]);

  const effectiveDbType = (dbType || '').toLowerCase();
  const isClickHouse =
    ['clickhouse', 'click_house', 'click house'].includes(effectiveDbType) ||
    effectiveDbType.includes('clickhouse') ||
    (activeCluster || '').toLowerCase().includes('clickhouse');
  const isMongo = ['mongodb', 'mongo'].includes(effectiveDbType);
  const isES = ['opensearch', 'elasticsearch'].includes(effectiveDbType);
  const isMongoOrES = isMongo || isES;
  const hidePerDb = isMongoOrES || isClickHouse;

  // Hover & Filter States
  const [hoveredTrend, setHoveredTrend] = useState<string | null>(null);
  const [hoveredPerDb, setHoveredPerDb] = useState<string | null>(null);
  // const [dbFilter, setDbFilter] = useState<string>('all');
  // const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = { cluster_id: activeCluster, minutes };
    if (clientId) params.client_id = clientId;

    const safeGet = async (url: string, p: any) => {
      try {
        const res = await baseapi.get(url, { params: p });
        return res.data?.data || res.data;
      } catch {
        return null;
      }
    };

    try {
      const [mRes, trendRes, perDbRes, sessRes] = await Promise.all([
        safeGet('/db_monitor_dashboard/metrics', params),
        safeGet('/db_monitor_dashboard/graph/connections', params),
        hidePerDb ? null : safeGet('/db_monitor_dashboard/graph/per_db/connections', params),
        isES ? null : safeGet('/db_monitor_dashboard/active_queries', { ...params, size: 200 }),
      ]);

      setMetricsData(mRes);
      setConnTrendData(trendRes);
      setPerDbConnData(perDbRes);
      setSessions(sessRes || []);
    } catch (err) {
      console.warn('ConnectionsTab fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId, isES, hidePerDb]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  // Trend graph series
  const activeKey = isMongo ? 'Active Ops' : isES ? 'Open Connections' : 'Active Sessions';
  const totalKey = isMongo ? 'Connected Clients' : isES ? 'Total Connections' : 'Total Sessions';

  const connTrendFormatted = useMemo(() => {
    if (!connTrendData || !connTrendData.labels) return [];
    const timeLabels = formatTimeLabels(connTrendData.labels);
    return connTrendData.labels.map((_: any, idx: number) => {
      const point: any = {
        time: timeLabels[idx],
        [activeKey]: connTrendData.series?.connections_active?.[idx] || 0,
        [totalKey]: connTrendData.series?.connections_total?.[idx] || 0,
      };
      if (!isClickHouse) {
        point.Waiting = connTrendData.series?.connections_waiting?.[idx] || 0;
      }
      return point;
    });
  }, [connTrendData, activeKey, totalKey, isClickHouse]);

  // Per DB connections series
  const perDbFormatted = useMemo(() => {
    if (hidePerDb || !perDbConnData || !perDbConnData.labels || !perDbConnData.databases) return [];
    const timeLabels = formatTimeLabels(perDbConnData.labels);
    return perDbConnData.labels.map((_: any, idx: number) => {
      const point: any = { time: timeLabels[idx] };
      perDbConnData.databases.forEach((db: string) => {
        point[db] = perDbConnData.series?.[`${db}:connections`]?.[idx] || 0;
      });
      return point;
    });
  }, [perDbConnData, hidePerDb]);
  // const dbOptions = useMemo(() => {
  //   return Array.from(new Set(sessions.map((s) => s.database).filter(Boolean))).sort();
  // }, [sessions]);

  // const dbDropdownData = useMemo(() => {
  //   return [
  //     { label: 'All Databases', value: 'all' },
  //     ...dbOptions.map((db) => ({ label: db, value: db })),
  //   ];
  // }, [dbOptions]);

  // const filteredSessions = useMemo(() => {
  //   return sessions.filter((s) => {
  //     if (dbFilter !== 'all' && s.database !== dbFilter) return false;
  //     if (searchQuery) {
  //       const q = searchQuery.toLowerCase();
  //       return (
  //         (s.database || '').toLowerCase().includes(q) ||
  //         (s.query || '').toLowerCase().includes(q) ||
  //         (s.user || s.service || '').toLowerCase().includes(q) ||
  //         (s.state || '').toLowerCase().includes(q)
  //       );
  //     }
  //     return true;
  //   });
  // }, [sessions, dbFilter, searchQuery]);

  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#06b6d4', '#ef4444'];
  const hasData = (arr: any[]) => arr && arr.length > 0;

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  const getDot = (_hovered?: any, _key?: any, _color?: any, _defaultR?: any) => false;

  const activeCount = metricsData?.connections_active ?? sessions.filter((s) => s.state === 'active').length;
  const idleCount = metricsData?.connections_idle ?? 0;
  const totalCount = metricsData?.connections_total ?? sessions.length;
  const maxConns = metricsData?.connections_max ?? 100;
  const utilPct = metricsData?.connections_util_pct ?? (maxConns > 0 ? (totalCount / maxConns) * 100 : 0);

  const formatUtilPct = (val: number | undefined | null) => {
    const num = Number(val || 0);
    if (num === 0) return '0%';
    if (num > 0 && num < 0.0001) return '<0.0001%';
    if (num < 1) {
      const str4 = num.toFixed(4);
      return `${Number(str4)}%`;
    }
    return `${Number(num.toFixed(2))}%`;
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[400px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }
  return (
    <div className="space-y-4 relative">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded bg-orange-500 text-white shadow-sm">
            <Radio className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-wide">
              {isMongo
                ? 'MongoDB Connection Activity'
                : 'Active Connections'}
            </h2>
          </div>
        </div>
      </div>

      {/* 5 Glassmorphic Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {/* Active / Ops Executing */}
        <div className="bg-emerald-50/60 dark:bg-emerald-600/10 border border-emerald-200 dark:border-green-500/30 rounded-xl p-3 shadow-sm dark:shadow-lg">
          <div className="flex items-center justify-between text-xs text-emerald-800 dark:text-gray-100 font-semibold mb-1">
            <span>{isMongo ? 'Ops Executing' : isES ? 'Open Connections' : 'Active'}</span>
            <Activity className="w-3.5 h-3.5 text-emerald-600 dark:text-green-400" />
          </div>
          <div className="text-xl font-bold font-mono text-emerald-600 dark:text-green-400">{activeCount}</div>
        </div>

        {/* Idle */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm dark:shadow-lg">
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-100 font-semibold mb-1">
            <span>Idle</span>
            <Clock className="w-3.5 h-3.5 text-gray-400" />
          </div>
          <div className="text-xl font-bold font-mono text-gray-800 dark:text-gray-300">{idleCount}</div>
        </div>

        {/* Idle in Txn */}
        {/* <div className="bg-amber-50/60 dark:bg-amber-600/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-3 shadow-sm dark:shadow-lg">
          <div className="flex items-center justify-between text-xs text-amber-800 dark:text-gray-100 font-semibold mb-1">
            <span>Idle in Txn</span>
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="text-xl font-bold font-mono text-amber-600 dark:text-amber-400">{waitingCount}</div>
        </div> */}

        {/* Total */}
        <div className="bg-blue-50/60 dark:bg-blue-600/10 border border-blue-200 dark:border-blue-500/30 rounded-xl p-3 shadow-sm dark:shadow-lg">
          <div className="flex items-center justify-between text-xs text-blue-800 dark:text-gray-100 font-semibold mb-1">
            <span>{isMongo ? 'Connected Clients' : 'Connections / Max'}</span>
            <DbIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="text-xl font-bold font-mono text-gray-900 dark:text-white">
            {totalCount}{' '}
            {!isMongo && <span className="text-xs text-gray-500 dark:text-gray-100 font-normal">/ {maxConns}</span>}
          </div>
        </div>

        {/* Utilization */}
        <div className="bg-orange-50/60 dark:bg-orange-600/10 border border-orange-200 dark:border-orange-500/30 rounded-xl p-3 shadow-sm dark:shadow-lg">
          <div className="flex items-center justify-between text-xs text-orange-800 dark:text-gray-100 font-semibold mb-1">
            <span>Connection utilization</span>
            <CheckCircle2 className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="text-xl font-bold font-mono text-orange-600 dark:text-orange-400">
            {formatUtilPct(utilPct)}
          </div>
        </div>
      </div>

      {/* Trend Graphs */}
      <div className={`grid grid-cols-1 ${hidePerDb ? '' : 'md:grid-cols-2'} gap-4`}>
        {/* Active vs Total Sessions */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
          <div className="mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-500 dark:text-blue-400" />
              <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                Running queries vs connections
              </h4>
            </div>
            <span className="text-[10px] font-mono text-gray-500 dark:text-gray-100">conns</span>
          </div>
          <div className="h-44 w-full flex items-center justify-center">
            {hasData(connTrendFormatted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={connTrendFormatted}>
                  <defs>
                    <linearGradient id="colorActiveConn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                  <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredTrend} onHoverSeries={setHoveredTrend} />} />
                  <Area
                    type="monotone"
                    dataKey={activeKey}
                    name={isMongo ? 'Active Ops' : isES ? 'Open Connections' : 'Active Sessions'}
                    stroke="#10b981"
                    strokeOpacity={getOpacity(hoveredTrend, activeKey)}
                    fillOpacity={getOpacity(hoveredTrend, activeKey) * 0.4}
                    fill="url(#colorActiveConn)"
                    strokeWidth={hoveredTrend === activeKey ? 2.5 : 1.5}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey={totalKey}
                    name={isMongo ? 'Connected Clients' : isES ? 'Total Connections' : 'Total Sessions'}
                    stroke="#3b82f6"
                    strokeOpacity={getOpacity(hoveredTrend, totalKey)}
                    strokeWidth={hoveredTrend === totalKey ? 2.5 : 1.5}
                    dot={false}
                  />
                  {!isMongo && !isClickHouse && (
                    <Line
                      type="monotone"
                      dataKey="Waiting"
                      name="Waiting"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredTrend, 'Waiting')}
                      strokeWidth={hoveredTrend === 'Waiting' ? 2.5 : 1.5}
                      dot={false}
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 dark:text-gray-100 italic">No connection trend data available</span>
            )}
          </div>
        </div>

        {/* Active Connections per Database (PG / MSSQL only) */}
        {!hidePerDb && (
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2 flex justify-between items-center">
              <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Connections per Database</h4>
              <span className="text-[10px] font-mono text-gray-500 dark:text-gray-100">conns</span>
            </div>
            <div className="h-44 w-full flex items-center justify-center">
              {hasData(perDbFormatted) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={perDbFormatted}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.2} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredPerDb} onHoverSeries={setHoveredPerDb} />} />
                    {(perDbConnData?.databases || []).map((db: string, i: number) => (
                      <Line
                        key={db}
                        type="monotone"
                        dataKey={db}
                        stroke={colors[i % colors.length]}
                        strokeOpacity={getOpacity(hoveredPerDb, db)}
                        strokeWidth={hoveredPerDb === db ? 2.5 : 1.5}
                        dot={getDot()}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-100 italic">No per-database connections telemetry available</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Active Sessions & Live Operations Table (PG, MSSQL, MongoDB only) */}
      {/* {!isES && (
        <div className="bg-white dark:bg-gray-900/80 border border-gray-200 dark:border-gray-700/60 rounded-xl shadow-sm dark:shadow-xl overflow-hidden mt-4">
       
          <div className="p-3 border-b border-gray-200 dark:border-gray-700/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50 dark:bg-gray-900/90">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-900 dark:text-white" />
              <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider font-mono">
                {isMongo ? 'Live Operations / Active Connections' : 'Active Queries'} ({filteredSessions.length})
              </h3>
            </div>

            <div className="flex items-center gap-2">
    
              {dbOptions.length > 0 && (
                <CustomDropdown
                  data={dbDropdownData}
                  selectedValue={dbFilter}
                  onSelect={(val) => setDbFilter(val)}
                  width="w-44"
                  buttonHeight={8}
                  placeholder="All Databases"
                />
              )}

        
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search query, database"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 font-mono w-48 sm:w-64"
                />
              </div>
            </div>
          </div>

     
          <div className="overflow-x-auto custom-scroll max-h-96">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700/80 text-[10px] font-bold text-gray-500 dark:text-gray-100 uppercase tracking-widest bg-gray-50 dark:bg-gray-900/90 sticky top-0 z-10">
                  <th className="py-2.5 px-3">DATABASE</th>
                  {!isClickHouse && <th className="py-2.5 px-3">STATE</th>}
                  <th className="py-2.5 px-3">{isMongo ? 'OPERATION / COMMAND' : 'QUERY'}</th>
                  <th className="py-2.5 px-3 text-right">DURATION</th>
          
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60 font-mono text-xs text-gray-700 dark:text-gray-200">
                {filteredSessions.length > 0 ? (
                  filteredSessions.map((q, idx) => {
                    const state = q.state || 'active';
                    const isAct = state === 'active';
                    const isIdleTxn = state.includes('idle in transaction');
                    const dur = q.duration_sec || 0;
                    const durStr = dur >= 1 ? `${dur.toFixed(1)}s` : `${(dur * 1000).toFixed(0)}ms`;
                    const durColor = dur > 10 ? 'text-rose-600 dark:text-red-400 font-bold' : dur > 3 ? 'text-amber-600 dark:text-amber-400 font-bold' : 'text-gray-700 dark:text-gray-200';


                    return (
                      <tr key={idx} className="hover:bg-orange-500/[0.06] transition-colors">
                        <td className="py-2 px-3 text-orange-600 dark:text-orange-400 font-bold">{q.database || '—'}</td>
                        {!isClickHouse && (
                          <td className="py-2 px-3">
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                isAct
                                  ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30'
                                  : isIdleTxn
                                  ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30'
                                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
                              }`}
                            >
                              {state}
                            </span>
                          </td>
                        )}
                        <td className="py-2 px-3 text-gray-700 dark:text-gray-300 max-w-xs truncate" title={q.query || ''}>
                          {q.query || '—'}
                        </td>
                        <td className={`py-2 px-3 text-right ${durColor}`}>{durStr}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={isClickHouse ? 3 : 4} className="py-8 text-center text-xs text-gray-500 dark:text-gray-100 italic">
                      No active sessions or live operations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )} */}
    </div>
  );
};
